import backendCall from "./AxiosService";

/**
 * Service to handle time tracking operations for requests and request groups
 */
export default {
  /**
   * Fetches current time data for an individual request
   * @param requestId - The ID of the request
   * @returns Promise with the current time data
   */
  async getRequestCurrentTime(requestId: number) {
    try {
      const response = await backendCall.get(`time/request/${requestId}/current`);
      return response.data;
    } catch (error) {
      console.error('Error fetching request current time:', error);
      throw error;
    }
  },

  /**
   * Fetches current time data for a request group
   * @param requestGroupId - The ID of the request group
   * @returns Promise with the current time data
   */
  async getRequestGroupCurrentTime(requestGroupId: number) {
    try {
      const response = await backendCall.get(`request-group/${requestGroupId}/current`);

      // Transform the response to match the expected format
      if (response.data && response.data.totalTime) {
        // Backend returns { totalTime: { isRunning, startTime, elapsedTime } }
        // Frontend expects { active: true/false, startTime, id? }
        return {
          success: true,
          data: {
            active: response.data.totalTime.isRunning,
            startTime: response.data.totalTime.startTime,
            // If we have a time record ID from the backend, include it
            id: response.data.timeRecordId || `temp-${Date.now()}`
          }
        };
      }

      // If no totalTime data, return inactive state
      return {
        success: true,
        data: {
          active: false
        }
      };
    } catch (error) {
      console.error('Error fetching request group current time:', error);
      throw error;
    }
  },

  /**
   * Fetches time records for a request
   * @param requestId - The ID of the request
   * @returns Promise with the time records
   */
  async getRequestTimeRecords(requestId: number) {
    try {
      console.log(`TimeTrackingService: Fetching time records for request ${requestId}`);
      const response = await backendCall.get(`requests/${requestId}/time-records`);
      console.log('TimeTrackingService: API response:', response);

      // Return the data from the response
      // The backend returns { status: 'success', data: [...] }
      return response.data;
    } catch (error) {
      console.error('Error fetching request time records:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        console.error('Error response:', error.response);
      }
      throw error;
    }
  },

  /**
   * Fetches time records for a request group
   * @param groupId - The ID of the request group
   * @returns Promise with the time records
   */
  async getRequestGroupTimeRecords(groupId: number) {
    try {
      const response = await backendCall.get(`request-groups/${groupId}/time-records`);
      return response.data;
    } catch (error) {
      console.error('Error fetching request group time records:', error);
      throw error;
    }
  },

  /**
   * Starts time tracking for a request
   * @param requestId - The ID of the request
   * @returns Promise with the start time data
   */
  async startRequestTime(requestId: number) {
    try {
      const data = { type: 'request', request: requestId };
      const response = await backendCall.post('time/start', data);
      return response.data;
    } catch (error) {
      console.error('Error starting request time:', error);
      throw error;
    }
  },

  /**
   * Ends time tracking for a request
   * @param requestId - The ID of the request
   * @returns Promise with the end time data
   */
  async endRequestTime(requestId: number) {
    try {
      const data = { type: 'request', request: requestId };
      const response = await backendCall.post('time/end', data);
      return response.data;
    } catch (error) {
      console.error('Error ending request time:', error);
      throw error;
    }
  },

  /**
   * Starts time tracking for a request group
   * @param requestGroupId - The ID of the request group
   * @returns Promise with the start time data
   */
  async startRequestGroupTime(requestGroupId: number) {
    try {
      console.log(`TimeTrackingService: Starting time for request group ${requestGroupId}`);
      const data = { type: 'request-group', requestGroup: requestGroupId };
      console.log('TimeTrackingService: Sending data to backend:', data);

      const response = await backendCall.post('time/start', data);
      console.log('TimeTrackingService: Start request group time API response:', response);

      // Ensure we're returning the data property of the response
      return response.data;
    } catch (error) {
      console.error('Error starting request group time:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        console.error('Error response:', error.response);
      }
      throw error;
    }
  },

  /**
   * Ends time tracking for a request group
   * @param requestGroupId - The ID of the request group
   * @returns Promise with the end time data
   */
  async endRequestGroupTime(requestGroupId: number) {
    try {
      console.log(`TimeTrackingService: Ending time for request group ${requestGroupId}`);
      const data = { type: 'request-group', requestGroup: requestGroupId };
      console.log('TimeTrackingService: Sending data to backend:', data);

      const response = await backendCall.post('time/end', data);
      console.log('TimeTrackingService: End request group time API response:', response);

      return response.data;
    } catch (error) {
      console.error('Error ending request group time:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        console.error('Error response:', error.response);
      }
      throw error;
    }
  },

  // Build time tracking methods

  /**
   * Fetches current time data for a build
   * @param buildId - The ID of the build
   * @returns Promise with the current time data
   */
  async getBuildCurrentTime(buildId: number) {
    try {
      const response = await backendCall.get(`time/build/${buildId}/current`);
      return response.data;
    } catch (error) {
      console.error('Error fetching build current time:', error);
      throw error;
    }
  },

  /**
   * Fetches time records for a build
   * @param buildId - The ID of the build
   * @returns Promise with the time records
   */
  async getBuildTimeRecords(buildId: number) {
    try {
      const response = await backendCall.get(`time/build/${buildId}/records`);
      return response.data;
    } catch (error) {
      console.error('Error fetching build time records:', error);
      throw error;
    }
  },

  /**
   * Starts time tracking for a build
   * @param buildId - The ID of the build
   * @returns Promise with the start time data
   */
  async startBuildTime(buildId: number) {
    try {
      const response = await backendCall.post(`time/build/${buildId}/start`);
      return response.data;
    } catch (error) {
      console.error('Error starting build time:', error);
      throw error;
    }
  },

  /**
   * Ends time tracking for a build
   * @param buildId - The ID of the build
   * @returns Promise with the end time data
   */
  async endBuildTime(buildId: number) {
    try {
      const response = await backendCall.post(`time/build/${buildId}/end`);
      return response.data;
    } catch (error) {
      console.error('Error ending build time:', error);
      throw error;
    }
  },

  /**
   * Check if a build has any time records
   * @param buildId - The ID of the build
   * @returns Promise with boolean indicating if records exist
   */
  async checkBuildHasTimeRecords(buildId: number) {
    try {
      const response = await backendCall.get(`time/build/${buildId}/has-records`);
      return response.data;
    } catch (error) {
      console.error('Error checking build time records:', error);
      throw error;
    }
  },

  /**
   * Edit a time record (update start/end times)
   * Restricted to admins, managers, supervisors, and dispatchers
   * @param timeRecordId - The ID of the time record
   * @param startTime - New start time
   * @param endTime - New end time
   * @returns Promise with the updated record
   */
  async editTimeRecord(timeRecordId: number, startTime: string, endTime: string) {
    try {
      const response = await backendCall.patch(`time/${timeRecordId}`, {
        startTime,
        endTime
      });
      return response.data;
    } catch (error) {
      console.error('Error editing time record:', error);
      throw error;
    }
  },

  /**
   * Delete a time record
   * Restricted to admins, managers, supervisors, and dispatchers
   * @param timeRecordId - The ID of the time record
   * @returns Promise with deletion result
   */
  async deleteTimeRecord(timeRecordId: number) {
    try {
      const response = await backendCall.delete(`time/${timeRecordId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting time record:', error);
      throw error;
    }
  },

  /**
   * Create a manual time record for a build
   * Restricted to admins, managers, supervisors, and dispatchers
   * @param buildId - The ID of the build
   * @param userId - The ID of the worker
   * @param startTime - Start time
   * @param endTime - End time
   * @returns Promise with the created record
   */
  async createManualBuildTimeRecord(buildId: number, userId: number, startTime: string, endTime: string) {
    try {
      const response = await backendCall.post(`time/build/${buildId}/manual`, {
        userId,
        startTime,
        endTime
      });
      return response.data;
    } catch (error) {
      console.error('Error creating manual build time record:', error);
      throw error;
    }
  }
}
